import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ConfigService, ScreenSize } from '@libs/shared-services';
import {
  asset,
  Coordinates,
  drawGumGradientsByCoordinates,
  drawTeeth,
  FONT_FAMILY,
  getMeasurementsCoordinates,
  GUM_INTERMEDIATE_BUMP,
  isReverse,
  Jaw,
  MeasurementCoordinates,
  offsetMeasurementCoordinatesY,
  PERIO_CIRCLE_RADIUS,
  PERIO_MEASUREMENT_DEFAULT_MEASUREMENT_COLOR,
  PERIO_MEASUREMENT_FONT_SIZE,
  PERIO_MEASUREMENT_FONT_WEIGHT,
  PERIO_MEASUREMENT_HEIGHT,
  PERIO_MEASUREMENT_HIGH_MEASUREMENT_COLOR,
  PERIO_MEASUREMENT_HIGH_THRESHOLD,
  PERIO_MEASUREMENT_MEDIUM_MEASUREMENT_COLOR,
  PERIO_MEASUREMENT_MEDIUM_THRESHOLD,
  PERIO_MEASUREMENT_RADIUS,
  PERIO_MEASUREMENT_TEXT_COLOR,
  PERIO_MEASUREMENT_WIDTH,
  Svg,
  Tooth,
  TOOTH_FULL_HEIGHT,
  TOOTH_HEIGHT,
  TOOTH_WIDTH,
  ToothSide,
  ToothWithLayers,
} from '@libs/ui-lib';
import { isNotNull } from '@libs/utils';
import { select } from 'd3-selection';

const CONTAINER_HEIGHT = 280;
const CONTAINER_WIDTH = 140;
const PERIO_MEASUREMENTS_PADDING_Y = 5;

@Component({
  selector: 'lib-tooth',
  templateUrl: 'tooth.component.html',
  styleUrls: ['./tooth.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToothComponent implements AfterViewInit, OnChanges {
  @ViewChild('chart', { static: true }) public chart: ElementRef | undefined;

  @Input({ required: true }) public tooth!: ToothWithLayers;
  @Input() public toothOnly = false;
  @Input() public cursorPointer = false;
  @Input() public inheritedMouseStyle = false;
  @Input() public isToothMissing = false;
  @Input() public showPerioMeasurements = false;

  public readonly configService: ConfigService = inject(ConfigService);
  public teethAssetBasePath = this.configService.config()?.teethAssetBasePath ?? '';

  private readonly screenSize: ScreenSize = 'xl';

  constructor(protected readonly renderer: Renderer2) {}

  public ngAfterViewInit(): void {
    this.draw();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['tooth'].isFirstChange()) {
      this.draw();
    }
  }

  private draw(): void {
    if (!this.chart) return;

    select(this.chart.nativeElement).select('svg').remove();

    const svg = select(this.chart.nativeElement)
      .append('svg')
      .attr(
        'viewBox',
        this.toothOnly
          ? `0 0 ${TOOTH_WIDTH(this.screenSize)} ${TOOTH_FULL_HEIGHT(this.screenSize)}`
          : `0 0 ${CONTAINER_WIDTH} ${CONTAINER_HEIGHT}`,
      );

    const toothCoordinates: Record<number, Coordinates> = {
      [this.tooth.position]: {
        x: this.toothOnly ? 0 : CONTAINER_WIDTH / 2 - TOOTH_WIDTH(this.screenSize) / 2,
        y: 0,
      },
    };

    if (this.toothOnly) {
      this.drawTooth(svg, this.tooth, toothCoordinates);
      return;
    }

    const perioTranslateY = (
      jaw: Jaw,
      side: ToothSide,
      coordinates: Coordinates,
      measurementsCoordinates: Coordinates,
      pocketDepth: number,
    ): number => {
      const MEASUREMENT_INCREMENTS = 3.5;
      const y = {
        upper: {
          buccal: coordinates.y + measurementsCoordinates.y - pocketDepth * MEASUREMENT_INCREMENTS,
          lingual: coordinates.y + measurementsCoordinates.y - pocketDepth * MEASUREMENT_INCREMENTS,
        },
        lower: {
          lingual:
            coordinates.y +
            measurementsCoordinates.y +
            pocketDepth * MEASUREMENT_INCREMENTS +
            TOOTH_FULL_HEIGHT(this.screenSize) -
            TOOTH_HEIGHT(this.screenSize),
          buccal:
            coordinates.y +
            measurementsCoordinates.y +
            pocketDepth * MEASUREMENT_INCREMENTS +
            TOOTH_FULL_HEIGHT(this.screenSize) -
            TOOTH_HEIGHT(this.screenSize),
        },
      };
      return y[jaw][side];
    };
    const gumIntermediaryBump = (jaw: Jaw, _: ToothSide): number => {
      return GUM_INTERMEDIATE_BUMP(this.screenSize) * (jaw === 'upper' ? 1 : -1);
    };

    const buccalPeriosCoordinates = getMeasurementsCoordinates(
      [this.tooth],
      toothCoordinates,
      'buccal',
      perioTranslateY,
      gumIntermediaryBump,
    );

    if (!this.isToothMissing) {
      drawGumGradientsByCoordinates(
        svg,
        { x: 0, y: 0 },
        { x: 0, y: CONTAINER_HEIGHT },
        { x: CONTAINER_WIDTH, y: 0 },
        { x: CONTAINER_WIDTH, y: CONTAINER_HEIGHT },
      );
    }

    this.drawTooth(svg, this.tooth, toothCoordinates);

    if (this.showPerioMeasurements) {
      this.drawPerioMeasurements(
        svg,
        [this.tooth],
        {
          [this.tooth.position]: { x: 0, y: this.tooth.jaw === 'upper' ? 0 : CONTAINER_HEIGHT - TOOTH_FULL_HEIGHT(this.screenSize) },
        },
        this.tooth.jaw,
        offsetMeasurementCoordinatesY(buccalPeriosCoordinates, PERIO_MEASUREMENTS_PADDING_Y * (this.tooth.jaw === 'upper' ? -1 : 1)),
      );
    }
  }

  private drawTooth(svg: Svg, tooth: Tooth, toothCoordinates: Record<number, Coordinates>): void {
    const toothAssetPath = (position: number) => `${asset(position)}/base/full.png`;
    drawTeeth({
      svg,
      teeth: [tooth],
      teethCoordinates: toothCoordinates,
      screenSize: this.screenSize,
      drawTreatments: true,
      height: TOOTH_FULL_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1)'),
      toothAssetPath,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: this.cursorPointer
        ? {
            click: () => {},
          }
        : undefined,
      useInheritedMouseStyle: true,
    });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private drawPerioMeasurements(
    svg: Svg,
    teeth: Tooth[],
    teethCoordinates: Record<number, Coordinates>,
    jaw: Jaw,
    perioCoordinates: MeasurementCoordinates[],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const paddingX = (CONTAINER_WIDTH - PERIO_MEASUREMENT_WIDTH * 3) / 2,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      threshold = 4,
      filteredPerioCoordinates = perioCoordinates.filter((el): el is MeasurementCoordinates & { pocketDepth: Coordinates } =>
        isNotNull(el.pocketDepth),
      );

    const isCloserThanWidth = filteredPerioCoordinates.some(({ pocketDepth }, i) => {
      return i > 0 && pocketDepth.x - filteredPerioCoordinates[i - 1].pocketDepth.x < PERIO_MEASUREMENT_WIDTH;
    });

    const jawMultiplier = jaw === 'upper' ? -1 : 1;
    const jawHeight = jaw === 'upper' ? PERIO_MEASUREMENT_HEIGHT : 0;

    teeth.forEach(({ position, perio }) => {
      const coordinates = teethCoordinates[position];
      const lingualMeasurements = [perio.lingual.pocketDepth.distal, perio.lingual.pocketDepth.central, perio.lingual.pocketDepth.mesial];

      lingualMeasurements.forEach((measurement, i) => {
        const x = isCloserThanWidth
          ? coordinates.x + paddingX + i * PERIO_MEASUREMENT_WIDTH
          : filteredPerioCoordinates[i + 1].pocketDepth.x - PERIO_MEASUREMENT_WIDTH / 2;
        const y = filteredPerioCoordinates[i + 1].pocketDepth.y + (jawHeight + PERIO_CIRCLE_RADIUS + threshold) * jawMultiplier;

        this.drawPerioMeasurement(svg, {
          x,
          y,
          measurement,
          fill:
            measurement >= PERIO_MEASUREMENT_HIGH_THRESHOLD
              ? PERIO_MEASUREMENT_HIGH_MEASUREMENT_COLOR
              : measurement >= PERIO_MEASUREMENT_MEDIUM_THRESHOLD
                ? PERIO_MEASUREMENT_MEDIUM_MEASUREMENT_COLOR
                : PERIO_MEASUREMENT_DEFAULT_MEASUREMENT_COLOR,
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          opacity: measurement < PERIO_MEASUREMENT_HIGH_THRESHOLD && measurement >= PERIO_MEASUREMENT_MEDIUM_THRESHOLD ? 0.8 : 0.64,
        });
      });

      const facialMeasurements = [perio.buccal.pocketDepth.distal, perio.buccal.pocketDepth.central, perio.buccal.pocketDepth.mesial];
      facialMeasurements.forEach((measurement, i) => {
        const x = isCloserThanWidth
          ? coordinates.x + paddingX + i * PERIO_MEASUREMENT_WIDTH
          : filteredPerioCoordinates[i + 1].pocketDepth.x - PERIO_MEASUREMENT_WIDTH / 2;
        const y =
          filteredPerioCoordinates[i + 1].pocketDepth.y +
          (jawHeight + PERIO_CIRCLE_RADIUS + threshold + PERIO_MEASUREMENT_HEIGHT + 2) * jawMultiplier;

        this.drawPerioMeasurement(svg, {
          x,
          y,
          measurement,
          fill: '#3D4A56',
          opacity: 0.4,
        });
      });
    });
  }

  private drawPerioMeasurement(
    svg: Svg,
    {
      x,
      y,
      measurement,
      fill,
      opacity,
    }: {
      x: number;
      y: number;
      measurement: number;
      fill: string;
      opacity: number;
    },
  ) {
    svg
      .append('rect')
      .attr('x', x)
      .attr('y', y)
      .attr('width', PERIO_MEASUREMENT_WIDTH)
      .attr('height', PERIO_MEASUREMENT_HEIGHT)
      .attr('rx', PERIO_MEASUREMENT_RADIUS)
      .attr('ry', PERIO_MEASUREMENT_RADIUS)
      .attr('fill', fill)
      .attr('opacity', opacity);

    svg
      .append('text')
      .attr('x', x + PERIO_MEASUREMENT_WIDTH / 2)
      .attr('y', y + PERIO_MEASUREMENT_HEIGHT / 2 + 1)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', PERIO_MEASUREMENT_TEXT_COLOR)
      .attr('font-family', FONT_FAMILY)
      .attr('font-size', PERIO_MEASUREMENT_FONT_SIZE)
      .attr('font-weight', PERIO_MEASUREMENT_FONT_WEIGHT)
      .attr('style', 'user-select: none; -webkit-user-select: none;')
      .text(measurement);
  }
}
